import { z } from "zod";
import { executeWithNetworkRetries } from "@metriport/shared";
import { apiOSS } from "./apiOSS";
import { isDevEnv } from "../shared/util";

const PATIENT_URL = `/medical/v1/patient`;

const suspectFhirSchema = z.object({
  code: z
    .object({
      coding: z.array(
        z.object({
          code: z.string(),
          display: z.string(),
          system: z.string(),
        })
      ),
      text: z.string(),
    })
    .optional(),
  effectiveDateTime: z.string().optional(),
  id: z.string(),
  resourceType: z.string(),
  status: z.string().optional(),
  valueQuantity: z
    .object({
      unit: z.string().optional().nullable(),
      value: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
  reasonCode: z
    .array(
      z.object({
        coding: z.array(
          z.object({
            code: z.string(),
            display: z.string(),
            system: z.string(),
          })
        ),
        text: z.string().optional(),
      })
    )
    .optional()
    .nullable(),
  authoredOn: z.string().optional(),
  intent: z.string().optional(),
  medicationCodeableConcept: z
    .object({
      coding: z.array(
        z.object({
          code: z.string(),
          display: z.string(),
          system: z.string(),
        })
      ),
      text: z.string(),
    })
    .optional(),
});

const suspectingEvidenceSchema = z.object({
  id: z.string(),
  resource_type: z.string(),
  data_source: z.string().optional(),
  fhir: suspectFhirSchema.optional(),
});

const suspectSchema = z
  .object({
    id: z.string(),
    cxId: z.string(),
    patientId: z.string(),
    group: z.string(),
    icd10Code: z.string(),
    icd10ShortDescription: z.string(),
    responsibleResources: z.array(suspectingEvidenceSchema),
    lastRun: z.string().transform(str => new Date(str)),
  })
  .transform(({ responsibleResources, ...rest }) => ({
    ...rest,
    suspectingEvidence: responsibleResources,
  }));

const suspectsResponseSchema = z.object({
  suspects: z.array(suspectSchema),
});

type SuspectFhir = z.infer<typeof suspectFhirSchema>;
type SuspectingEvidence = z.infer<typeof suspectingEvidenceSchema>;
type Suspect = z.infer<typeof suspectSchema>;
type SuspectsResponse = z.infer<typeof suspectsResponseSchema>;

const SANDBOX_MOCK_DATA = {
  suspects: [
    {
      id: "05759f3c-5878-4748-9195-ea0ae3dc09ee",
      createdAt: "2025-10-13T19:53:47.600Z",
      updatedAt: "2025-10-13T19:53:47.600Z",
      version: 0,
      cxId: "c4f7f1c3-f206-4ba3-a410-5050c7003dfb",
      patientId: "01986894-efcd-7aaf-b1b7-382bdb4c7f64",
      group: "bipolar_lithium_signal",
      icd10Code: "F31.9",
      icd10ShortDescription: "Bipolar disorder, unspecified (screen signal)",
      responsibleResources: [
        {
          id: "9a1b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d",
          fhir: {
            id: "9a1b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d",
            code: {
              text: "Lithium, Blood [Moles/volume] in Serum or Plasma",
              coding: [
                {
                  code: "14334-7",
                  system: "http://loinc.org",
                  display: "Lithium, Blood [Moles/volume] in Serum or Plasma",
                },
              ],
            },
            status: "final",
            resourceType: "Medication",
            valueQuantity: {
              unit: "mmol/L",
              value: 0.6,
            },
            effectiveDateTime: "2021-12-21",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Medication",
        },
      ],
      lastRun: "2025-10-13T19:53:47.600Z",
    },
    {
      id: "0199747f-0ccc-7ba0-a076-c299d1e31f1b",
      createdAt: "2025-09-23T02:55:07.468Z",
      updatedAt: "2025-09-23T02:55:07.468Z",
      version: 0,
      cxId: "c4f7f1c3-f206-4ba3-a410-5050c7003dfb",
      patientId: "01986894-efcd-7aaf-b1b7-382bdb4c7f64",
      group: "depression_phq9_10plus",
      icd10Code: "F32.A",
      icd10ShortDescription: "Depression, unspecified (screen positive)",
      responsibleResources: [
        {
          id: "8b2c3d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e",
          fhir: {
            id: "8b2c3d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e",
            code: {
              text: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
              coding: [
                {
                  code: "44261-6",
                  system: "http://loinc.org",
                  display: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "score",
              value: 11,
            },
            effectiveDateTime: "2016-09-22",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
        {
          id: "7c3d4e5f-6a7b-8c9d-0e1f-2a3b4c5d6e7f",
          fhir: {
            id: "7c3d4e5f-6a7b-8c9d-0e1f-2a3b4c5d6e7f",
            code: {
              text: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
              coding: [
                {
                  code: "44261-6",
                  system: "http://loinc.org",
                  display: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "score",
              value: 11,
            },
            effectiveDateTime: "2016-06-22",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
        {
          id: "6d4e5f6a-7b8c-9d0e-1f2a-3b4c5d6e7f8a",
          fhir: {
            id: "6d4e5f6a-7b8c-9d0e-1f2a-3b4c5d6e7f8a",
            code: {
              text: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
              coding: [
                {
                  code: "44261-6",
                  system: "http://loinc.org",
                  display: "Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "score",
              value: 11,
            },
            effectiveDateTime: "2016-03-22",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
      ],
      lastRun: "2025-09-23T02:55:05.950Z",
    },
    {
      id: "00b5d549-1e22-4b60-a4ac-24b35b7dbcc8",
      createdAt: "2025-10-13T19:53:47.600Z",
      updatedAt: "2025-10-13T19:53:47.600Z",
      version: 0,
      cxId: "c4f7f1c3-f206-4ba3-a410-5050c7003dfb",
      patientId: "01986894-efcd-7aaf-b1b7-382bdb4c7f64",
      group: "hf_combined_suspect",
      icd10Code: "I50.9",
      icd10ShortDescription: "Heart failure, unspecified",
      responsibleResources: [
        {
          id: "a8f3d2e1-4b7c-4a9d-8e6f-1c2b3d4e5f6a",
          fhir: {
            id: "a8f3d2e1-4b7c-4a9d-8e6f-1c2b3d4e5f6a",
            code: {
              text: "Echocardiography, transthoracic, real-time with image documentation (2D), includes M-mode recording, when performed, complete, with spectral Doppler echocardiography, and with color flow Doppler echocardiography",
              coding: [
                {
                  code: "93306",
                  system: "http://www.ama-assn.org/go/cpt",
                  display:
                    "Echocardiography, transthoracic, real-time with image documentation (2D), includes M-mode recording, when performed, complete, with spectral Doppler echocardiography, and with color flow Doppler echocardiography",
                },
              ],
            },
            status: "final",
            resourceType: "Procedure",
            effectiveDateTime: "2016-06-28",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Procedure",
        },
        {
          id: "b9e4c3f2-5c8d-4b0e-9f7a-2d3c4e5f6a7b",
          fhir: {
            id: "b9e4c3f2-5c8d-4b0e-9f7a-2d3c4e5f6a7b",
            code: {
              text: "Echocardiography, transthoracic, real-time with image documentation (2D), includes M-mode recording, when performed, complete, with spectral Doppler echocardiography, and with color flow Doppler echocardiography",
              coding: [
                {
                  code: "93306",
                  system: "http://www.ama-assn.org/go/cpt",
                  display:
                    "Echocardiography, transthoracic, real-time with image documentation (2D), includes M-mode recording, when performed, complete, with spectral Doppler echocardiography, and with color flow Doppler echocardiography",
                },
              ],
            },
            status: "final",
            resourceType: "Procedure",
            effectiveDateTime: "2013-12-06",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Procedure",
        },
        {
          id: "c0f5d4e3-6d9e-4c1f-0a8b-3e4d5f6a7b8c",
          fhir: {
            id: "c0f5d4e3-6d9e-4c1f-0a8b-3e4d5f6a7b8c",
            code: {
              text: "Other fatigue",
              coding: [
                {
                  code: "R5383",
                  system: "http://hl7.org/fhir/sid/icd-10-cm",
                  display: "Other fatigue",
                },
              ],
            },
            status: "final",
            resourceType: "Condition",
            effectiveDateTime: "2024-05-20",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Condition",
        },
        {
          id: "d1a6e5f4-7e0f-4d2a-1b9c-4f5e6a7b8c9d",
          fhir: {
            id: "d1a6e5f4-7e0f-4d2a-1b9c-4f5e6a7b8c9d",
            code: {
              text: "Other fatigue",
              coding: [
                {
                  code: "R5383",
                  system: "http://hl7.org/fhir/sid/icd-10-cm",
                  display: "Other fatigue",
                },
              ],
            },
            status: "final",
            resourceType: "Condition",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Condition",
        },
        {
          id: "e2b7f6a5-8f1a-4e3b-2c0d-5a6b7c8d9e0f",
          fhir: {
            id: "e2b7f6a5-8f1a-4e3b-2c0d-5a6b7c8d9e0f",
            code: {
              text: "Chest pain, unspecified",
              coding: [
                {
                  code: "R079",
                  system: "http://hl7.org/fhir/sid/icd-10-cm",
                  display: "Chest pain, unspecified",
                },
              ],
            },
            status: "final",
            resourceType: "Condition",
            effectiveDateTime: "2016-03-26",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Condition",
        },
        {
          id: "f3c8a7b6-9a2b-4f4c-3d1e-6b7c8d9e0f1a",
          fhir: {
            id: "f3c8a7b6-9a2b-4f4c-3d1e-6b7c8d9e0f1a",
            code: {
              text: "Chest pain, unspecified",
              coding: [
                {
                  code: "R079",
                  system: "http://hl7.org/fhir/sid/icd-10-cm",
                  display: "Chest pain, unspecified",
                },
              ],
            },
            status: "final",
            resourceType: "Condition",
            effectiveDateTime: "2021-03-08",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Condition",
        },
      ],
      lastRun: "2025-10-13T19:53:47.600Z",
    },
    {
      id: "0199747f-1339-7f85-a49d-57be7f9f41af",
      createdAt: "2025-09-23T02:55:09.113Z",
      updatedAt: "2025-09-23T02:55:09.113Z",
      version: 0,
      cxId: "c4f7f1c3-f206-4ba3-a410-5050c7003dfb",
      patientId: "01986894-efcd-7aaf-b1b7-382bdb4c7f64",
      group: "morbid_obesity",
      icd10Code: "E66.01",
      icd10ShortDescription: "Morbid (severe) obesity due to excess calories",
      responsibleResources: [
        {
          id: "5e6f7a8b-9c0d-1e2f-3a4b-5c6d7e8f9a0b",
          fhir: {
            id: "5e6f7a8b-9c0d-1e2f-3a4b-5c6d7e8f9a0b",
            code: {
              text: "Body mass index (BMI) [Ratio]",
              coding: [
                {
                  code: "39156-5",
                  system: "http://loinc.org",
                  display: "Body mass index (BMI) [Ratio]",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "kg/m2",
              value: 40.4,
            },
            effectiveDateTime: "2021-10-02",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
      ],
      lastRun: "2025-09-23T02:55:07.553Z",
    },
    {
      id: "019962cd-13f0-7e56-a10c-0ebbbf52a93a",
      createdAt: "2025-09-19T16:27:11.217Z",
      updatedAt: "2025-09-19T16:27:11.217Z",
      version: 0,
      cxId: "c4f7f1c3-f206-4ba3-a410-5050c7003dfb",
      patientId: "01986894-efcd-7aaf-b1b7-382bdb4c7f64",
      group: "stage2_systolic",
      icd10Code: "I10",
      icd10ShortDescription: "Essential (primary) hypertension",
      responsibleResources: [
        {
          id: "4f7a8b9c-0d1e-2f3a-4b5c-6d7e8f9a0b1c",
          fhir: {
            id: "4f7a8b9c-0d1e-2f3a-4b5c-6d7e8f9a0b1c",
            code: {
              text: "Diastolic blood pressure",
              coding: [
                {
                  code: "8462-4",
                  display: "Diastolic blood pressure",
                  system: "http://loinc.org",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "mmHg",
              value: 80,
            },
            effectiveDateTime: "2020-02-27",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
        {
          id: "3a8b9c0d-1e2f-3a4b-5c6d-7e8f9a0b1c2d",
          fhir: {
            id: "3a8b9c0d-1e2f-3a4b-5c6d-7e8f9a0b1c2d",
            code: {
              text: "Diastolic blood pressure",
              coding: [
                {
                  code: "8462-4",
                  display: "Diastolic blood pressure",
                  system: "http://loinc.org",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "mmHg",
              value: 89,
            },
            effectiveDateTime: "2015-04-25",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
        {
          id: "2b9c0d1e-2f3a-4b5c-6d7e-8f9a0b1c2d3e",
          fhir: {
            id: "2b9c0d1e-2f3a-4b5c-6d7e-8f9a0b1c2d3e",
            code: {
              text: "Diastolic blood pressure",
              coding: [
                {
                  code: "8462-4",
                  display: "Diastolic blood pressure",
                  system: "http://loinc.org",
                },
              ],
            },
            status: "final",
            resourceType: "Observation",
            valueQuantity: {
              unit: "mmHg",
              value: 88,
            },
            effectiveDateTime: "2024-03-08",
          },
          data_source:
            "c4f7f1c3-f206-4ba3-a410-5050c7003dfb/01986894-efcd-7aaf-b1b7-382bdb4c7f64/c4f7f1c3-f206-4ba3-a410-5050c7003dfb_01986894-efcd-7aaf-b1b7-382bdb4c7f64_jane1.xml",
          resource_type: "Observation",
        },
      ],
      lastRun: "2025-09-19T16:27:08.863Z",
    },
  ],
};

/**
 * Checks if the current environment is sandbox and returns appropriate suspects data.
 * In sandbox mode and if the user is a Metriport user, returns mock data. Otherwise, fetches from API.
 */
export async function getSuspects(
  patientId: string,
  isMetriportUser: boolean,
  isUserInSandbox: boolean
): Promise<SuspectsResponse> {
  const isUseDummyData = isUserInSandbox || isDevEnv();

  if (isUseDummyData && isMetriportUser) {
    return suspectsResponseSchema.parse(SANDBOX_MOCK_DATA);
  }

  const resp = await executeWithNetworkRetries(
    () => apiOSS.get(`${PATIENT_URL}/${patientId}/suspect`),
    { maxAttempts: 5, initialDelay: 100 }
  );

  const suspectsData = suspectsResponseSchema.parse(resp.data);

  return suspectsData;
}

export type { SuspectFhir, SuspectingEvidence, Suspect, SuspectsResponse };
